import { AddOutlined, AssessmentOutlined, DeleteOutlined, EditOutlined } from '@mui/icons-material';
import { alpha, Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import AssessmentBillingDialog from './assessmentBillingDialog';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import { SectionCard } from 'components/v2/common/sectionCard';

import useTranslate from 'hooks/useTranslate';
import type { AssessmentBillingRule } from 'types';
import { formatCurrency } from 'utils/formatters';
import { useAssessmentBilling } from 'views/v2/serviceProviderBilling/hooks/useAssessmentBilling';

interface AssessmentBillingCardProps {
    providerId: number;
}

const AssessmentBillingCard = ({ providerId }: AssessmentBillingCardProps) => {
    const { translate } = useTranslate();
    const { config, loading, save } = useAssessmentBilling(providerId);
    const [editing, setEditing] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [saving, setSaving] = useState(false);
    const rule = config?.assessment ?? null;

    const saveRule = async (assessment: AssessmentBillingRule | null) => {
        setSaving(true);
        const saved = await save(assessment);
        setSaving(false);
        if (saved) {
            setEditing(false);
            setRemoving(false);
        }
    };

    const actions = loading ? null : (
        <>
            {rule ? (
                <Button
                    size="small"
                    color="inherit"
                    startIcon={<DeleteOutlined />}
                    onClick={() => setRemoving(true)}
                    sx={{ textTransform: 'none' }}
                >
                    {translate('serviceProviderBilling.assessment.remove')}
                </Button>
            ) : null}
            <Button
                size="small"
                variant={rule ? 'outlined' : 'contained'}
                startIcon={rule ? <EditOutlined /> : <AddOutlined />}
                onClick={() => setEditing(true)}
                sx={{ textTransform: 'none' }}
            >
                {translate(
                    rule
                        ? 'serviceProviderBilling.assessment.edit'
                        : 'serviceProviderBilling.assessment.set'
                )}
            </Button>
        </>
    );

    const content = (() => {
        if (loading) {
            return (
                <Stack spacing={1.5}>
                    <Skeleton variant="text" width={180} height={40} />
                    <Skeleton variant="rounded" width="55%" height={24} />
                </Stack>
            );
        }
        if (!rule) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {translate('serviceProviderBilling.assessment.unconfigured')}
                </Typography>
            );
        }
        return (
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <Box
                    sx={{
                        display: 'grid',
                        placeItems: 'center',
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        color: 'primary.main',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    }}
                >
                    <AssessmentOutlined />
                </Box>
                <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {formatCurrency(rule.price, rule.currency)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {translate('serviceProviderBilling.assessment.perCompletedAssessment')}
                        </Typography>
                    </Stack>
                    <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={translate('serviceProviderBilling.assessment.fullCollection')}
                        sx={{ alignSelf: 'flex-start' }}
                    />
                </Stack>
            </Stack>
        );
    })();

    return (
        <>
            <SectionCard
                title={translate('serviceProviderBilling.assessment.title')}
                actions={actions}
            >
                {content}
            </SectionCard>
            {editing ? (
                <AssessmentBillingDialog
                    rule={rule}
                    loading={saving}
                    onClose={() => setEditing(false)}
                    onSubmit={saveRule}
                />
            ) : null}
            {removing ? (
                <ConfirmationDialog
                    title={translate('serviceProviderBilling.assessment.removeTitle')}
                    content={translate('serviceProviderBilling.assessment.removeContent')}
                    loading={saving}
                    onClose={() => setRemoving(false)}
                    onConfirm={() => saveRule(null)}
                />
            ) : null}
        </>
    );
};

export default AssessmentBillingCard;
